// @flow

import { getComponentSettings } from './main';

import { type State } from '../reducers';
import { type State as Pdb } from '../../pdb/types';
import { buildDeliveryText } from '../../../components/carBlock/helpers';
import type { State as RouterState } from '../../router/types';
import {
  getShortestDeliveryTime,
} from '../../pdb/selectors';

type GlobalState = {
  pdb: Pdb,
  componentSettings: State,
  router: RouterState,
};

export type CarBlockSettings = $PropertyType<State, 'carblock'>;
export const getCarBlockSettings = (state: GlobalState): CarBlockSettings => {
  const carBlock = getComponentSettings(state).carblock;
  if (!carBlock) {
    throw new Error('Missing carblock settings in componentSettings store.');
  }
  return carBlock;
};

type ButtonText = $PropertyType<CarBlockSettings, 'button_text'>;
export const getButtonText = (state: GlobalState): ButtonText => (
  getCarBlockSettings(state).button_text
);

type PriceLabel = $PropertyType<CarBlockSettings, 'price_label'>;
export const getPriceLabel = (state: GlobalState): PriceLabel => (
  getCarBlockSettings(state).price_label
);

type PriceDuration = $PropertyType<CarBlockSettings, 'price_duration'>;
export const getPriceDuration = (state: GlobalState): PriceDuration => (
  getCarBlockSettings(state).price_duration
);

type ContractDurationAbbrv = $PropertyType<CarBlockSettings, 'contract_duration_abbrv'>;
export const getContractDurationAbbrv = (state: GlobalState): ContractDurationAbbrv => (
  getCarBlockSettings(state).contract_duration_abbrv
);

type MileageAbbrv = $PropertyType<CarBlockSettings, 'mileage_abbrv'>;
export const getMileageAbbrv = (state: GlobalState): MileageAbbrv => (
  getCarBlockSettings(state).mileage_abbrv
);

type YearAbbrv = $PropertyType<CarBlockSettings, 'year_abbrv'>;
export const getYearAbbrv = (state: GlobalState): YearAbbrv => (
  getCarBlockSettings(state).year_abbrv
);

type AndAbbrv = $PropertyType<CarBlockSettings, 'and_abbrv'>;
export const getAndAbbrv = (state: GlobalState): AndAbbrv => (
  getCarBlockSettings(state).and_abbrv
);

type PerAbbrv = $PropertyType<CarBlockSettings, 'per_abbrv'>;
export const getPerAbbrv = (state: GlobalState): PerAbbrv => (
  getCarBlockSettings(state).per_abbrv
);

type RoadtaxNote = $PropertyType<CarBlockSettings, 'roadtax_note'>;
export const getRoadtaxNote = (state: GlobalState): RoadtaxNote => (
  getCarBlockSettings(state).roadtax_note
);

type ContractPostfixText = $PropertyType<CarBlockSettings, 'contract_postfix'>;
export const getContractPostfixText = (state: GlobalState): ContractPostfixText => (
  getCarBlockSettings(state).contract_postfix
);

type ContractPrefixText = $PropertyType<CarBlockSettings, 'contract_prefix'>;
export const getContractPrefixText = (state: GlobalState): ContractPrefixText => (
  getCarBlockSettings(state).contract_prefix
);

type DeliveryTime = $PropertyType<CarBlockSettings, 'delivery_time'>;
export const getDeliveryTime = (state: GlobalState): DeliveryTime => (
  getCarBlockSettings(state).delivery_time
);

type DeliveryTimeApproximately = $PropertyType<CarBlockSettings, 'delivery_time_approximately'>;
export const getDeliveryTimeApproximately = (state: GlobalState): DeliveryTimeApproximately => (
  getCarBlockSettings(state).delivery_time_approximately
);

type DeliveryTimeDurationSingle = $PropertyType<CarBlockSettings, 'delivery_time_duration_single'>;
export const getDeliveryTimeDurationSingle = (state: GlobalState): DeliveryTimeDurationSingle => (
  getCarBlockSettings(state).delivery_time_duration_single
);

type DeliveryTimeDurationPlural = $PropertyType<CarBlockSettings, 'delivery_time_duration_plural'>;
export const getDeliveryTimeDurationPlural = (state: GlobalState): DeliveryTimeDurationPlural => (
  getCarBlockSettings(state).delivery_time_duration_plural
);

type Reserved = $PropertyType<CarBlockSettings, 'reserved_text'>;
export const getReserved = (state: GlobalState): Reserved => (
  getCarBlockSettings(state).reserved_text
);

type NotAvailableText = $PropertyType<CarBlockSettings, 'not_available_text'>;
export const getNotAvailableText = (state: GlobalState): NotAvailableText => (
  getCarBlockSettings(state).not_available_text
);

type DeliveryText = string;
export const getDeliveryText = (
  state: GlobalState,
  pdbId: string,
  packageId?: string = '',
): DeliveryText => {
  const deliveryTime = getShortestDeliveryTime(state, pdbId, packageId);

  return !deliveryTime ? '' : buildDeliveryText({
    approximatelyText: getDeliveryTimeApproximately(state),
    deliveryTime,
    deliveryTimeText: getDeliveryTime(state),
    timeUnitSingle: getDeliveryTimeDurationSingle(state),
    timeUnitPlural: getDeliveryTimeDurationPlural(state),
  });
};

export default null;
